import { useState } from "react";
import { toast } from "sonner";
import { ShieldAlert, Lock } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { DISCLOSURE_QS } from "@/components/apply/HealthFields";
import { fmtMT } from "@/lib/mountainTime";

const ans = (v) => (v === "yes" ? "Yes" : v === "no" ? "No" : "Not answered");

function Original({ o }) {
  return (
    <div data-testid="disclosure-original" className="rounded-lg bg-amber-50/60 border border-amber-200 p-3 text-sm text-stone-800 space-y-2">
      <div className="flex items-center gap-2 text-xs font-semibold text-amber-900"><Lock className="h-3.5 w-3.5" />SELLER'S ORIGINAL DISCLOSURE — preserved</div>
      {o ? DISCLOSURE_QS.map(({ q, d, short }) => (
        <p key={q}><strong>{short}:</strong> {ans(o[q])}{o[q] === "yes" && o[d] ? <span className="block whitespace-pre-wrap text-stone-700">{o[d]}</span> : null}</p>
      )) : <p className="text-stone-500 italic">The seller did not answer these questions (application submitted before they were added).</p>}
    </div>
  );
}

export function DisclosureReview({ horseId, horse, onSaved }) {
  const cur = horse.disclosures || {};
  const [v, setV] = useState(() => Object.fromEntries(DISCLOSURE_QS.flatMap(({ q, d }) => [[q, cur[q] || ""], [d, cur[d] || ""]])));
  const [approved, setApproved] = useState(!!cur.approved_for_ad);
  const [saving, setSaving] = useState(false);
  const pick = (q, d, x) => {
    if (x === "no" && (v[d] || "").trim() && !window.confirm("Changing this to No will clear the description. Continue?")) return;
    setV((s) => ({ ...s, [q]: x, ...(x === "no" ? { [d]: "" } : {}) }));
  };
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/horses/${horseId}/disclosures`, { ...v, approved_for_ad: approved });
      onSaved(data); toast.success(approved ? "Disclosures reviewed and approved for the ad" : "Disclosures saved — not shown in the ad");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save disclosures"); }
    setSaving(false);
  };
  return (
    <div data-testid="disclosure-review" className="mt-5 border-t border-stone-100 pt-4 space-y-3">
      <div className="flex items-center gap-2 text-sm font-semibold text-stone-900"><ShieldAlert className="h-4 w-4 text-amber-800" />Horse Health &amp; Behaviour</div>
      <Original o={cur.seller_original} />
      {DISCLOSURE_QS.map(({ q, d, short, descLabel }) => (
        <div key={q} className="space-y-2">
          <div className="text-[11px] uppercase tracking-wider text-stone-400 font-semibold">{short}</div>
          <div className="flex gap-2">{["yes", "no"].map((x) => (
            <button key={x} type="button" data-testid={`disclosure-${q}-${x}`} onClick={() => pick(q, d, x)}
              className={`h-9 px-4 rounded-md border text-sm font-semibold ${v[q] === x ? "bg-amber-800 border-amber-800 text-white" : "bg-white border-stone-200 text-stone-700"}`}>{x === "yes" ? "Yes" : "No"}</button>
          ))}</div>
          {v[q] === "yes" && <Textarea data-testid={`disclosure-${d}`} aria-label={descLabel} placeholder={descLabel} rows={3} value={v[d]}
            onChange={(e) => setV((s) => ({ ...s, [d]: e.target.value }))} className="text-sm border-stone-200 focus-visible:ring-amber-800" />}
        </div>
      ))}
      <label className="flex items-start gap-2 text-sm text-stone-700 cursor-pointer">
        <Checkbox data-testid="disclosure-approved" checked={approved} onCheckedChange={(x) => setApproved(!!x)} className="mt-0.5" />
        <span>Reviewed — include these disclosures in the published ad. Unchecked disclosures stay internal.</span>
      </label>
      <div className="flex flex-wrap items-center gap-3">
        <Button data-testid="disclosure-save" size="sm" disabled={saving} onClick={save} className="rounded-full bg-amber-800 hover:bg-amber-900 text-white">{saving ? "Saving…" : "Save Disclosures"}</Button>
        <span data-testid="disclosure-status" className="text-xs text-stone-500">
          {cur.reviewed_at ? `${cur.approved_for_ad ? "Approved for ad" : "Not in ad"} · reviewed ${fmtMT(cur.reviewed_at)} by ${cur.reviewed_by}` : "Awaiting admin review — not shown in the ad"}
        </span>
      </div>
    </div>
  );
}
